import { ISkuPackaging } from "fieldpro-tools";
import _ from "lodash";

import { IAction } from "redux/store/model";

import {
  FetchStockMovementsSuccessAction,
  IDeleteStockMovementSuccessAction,
  UpdateStockMovementStatusSuccessActionCreator,
} from "./actionCreators";
import * as types from "./actionTypes";
import initialState from "./initialState";

export interface IMovementRequestedItem {
  id: string;
  sku_id: string;
  sku_name?: string;
  requested_quantity: number;
  packaging: ISkuPackaging;
}

export interface IStockMovementState {
  stock_movements: IFrontendStockMovement[];
  loading: boolean;
}

export interface ILightStockMovement {
  id: string;
  requested: Array<IMovementRequestedItem>;
}

export interface IFrontendStockMovement extends ILightStockMovement {
  user_id: string;
  user_name?: string;

  to_storage_id: string;
  to_storage_name?: string;
  from_storage_id?: string;
  from_storage_name?: string;

  mode: string;
  status: string;
  created_at?: Date;
  updated_at?: Date;
  created_by: string;
  updated_by: string;
}

export default function reducer(
  state = initialState,
  action: IAction,
): IStockMovementState {
  switch (action.type) {
    case types.UPDATE_STOCK_MOVEMENT_BEGIN:
    case types.FETCH_STOCK_MOVEMENTS_BEGIN:
    case types.DELETE_STOCK_MOVEMENT_BEGIN:
      return {
        ...state,
        loading: true,
      };

    case types.UPDATE_STOCK_MOVEMENT_FAILURE:
    case types.FETCH_STOCK_MOVEMENTS_FAILURE:
    case types.DELETE_STOCK_MOVEMENT_FAILURE:
      return {
        ...state,
        loading: false,
      };

    case types.DELETE_STOCK_MOVEMENT_SUCCESS: {
      const { deletedId } = action as IDeleteStockMovementSuccessAction;
      return {
        ...state,
        loading: false,
        stock_movements: state.stock_movements.filter(
          (movement) => movement.id !== deletedId,
        ),
      };
    }

    case types.FETCH_STOCK_MOVEMENTS_SUCCESS: {
      const { stock_movements, append } =
        action as FetchStockMovementsSuccessAction;

      let newStockMovements: IFrontendStockMovement[] = stock_movements;
      if (append) {
        newStockMovements = _.concat(state.stock_movements, stock_movements);
      }

      return {
        ...state,
        loading: false,
        stock_movements: newStockMovements,
      };
    }

    case types.UPDATE_STOCK_MOVEMENT_SUCCESS: {
      const { stock_movements: stockMovements } =
        action as UpdateStockMovementStatusSuccessActionCreator;

      // Rebuild immutably: mutating in place keeps the same refs and leaves the
      // detail view stale after an edit.
      const newStockMovements = state.stock_movements.map((movement) => {
        const updated = _.find(stockMovements, { id: movement.id });
        if (!updated) {
          return movement;
        }
        return {
          ...movement,
          ...(updated.status ? { status: updated.status } : {}),
          // A status-only update omits `requested`; keep the existing lines
          // instead of wiping them to [].
          ...(updated.requested
            ? {
                requested: _.map(updated.requested, (item) => {
                  const existingItem = _.find(movement.requested, {
                    sku_id: item.sku_id,
                  });
                  return {
                    ...existingItem,
                    id: item.id ?? existingItem?.id ?? "",
                    sku_id: item.sku_id,
                    requested_quantity: item.requested_quantity,
                    packaging: item.packaging,
                  };
                }),
              }
            : {}),
        };
      });

      return {
        ...state,
        loading: false,
        stock_movements: newStockMovements,
      };
    }

    default:
      return state;
  }
}
